import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';

const HomePage = () => {
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);

  return (
    <>
      <SEOHead 
        title="Logistiat - Fleet Management Software Saudi Arabia"
        description="Complete fleet management system for Saudi Arabia trucking companies. Shipment tracking, trip costing, maintenance, and driver management with Arabic/English support."
        keywords="fleet management Saudi Arabia, logistics software KSA, trucking software, TMS Saudi Arabia, transportation management system, shipment tracking, fuel management"
        canonicalUrl="/en"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Logistiat",
          "url": "https://logistiat.app",
          "description": "Fleet management software for Saudi Arabia",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://logistiat.app/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <main className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Logistiat</h1>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</Link>
              <Link to="/trip-costing" className="text-gray-600 hover:text-gray-900">Trip Costing</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900">Contact</Link>
              <Link to="/ar" className="text-gray-600 hover:text-gray-900">العربية</Link>
            </div>
            <div className="flex space-x-4">
              <Link
                to="/login"
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 font-medium"
              >
                Login
              </Link>
              <button
                onClick={() => setIsTrialModalOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Fleet Management Software Saudi Arabia
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Complete fleet management system for KSA trucking companies. Track shipments, calculate trip costs, manage maintenance, and handle driver settlements in Arabic and English.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsTrialModalOpen(true)}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 shadow-lg"
                >
                  Start Free Trial
                </button>
                <Link
                  to="/login"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 text-center"
                >
                  Login for Existing Customers
                </Link>
              </div>
            </div>
            
            {/* Shipment Tracking Screenshot */}
            <div className="relative">
              <div className="bg-white rounded-xl shadow-2xl p-4">
                <img
                  src="/images/shipment-tracking.png"
                  alt="Logistiat Shipment Tracking Dashboard"
                  className="rounded-lg w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Live Demo
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trucking Fleet Software Saudi
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage your fleet operations efficiently
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">🚛</div>
              <h3 className="text-xl font-semibold mb-3">Shipment Management Software Saudi</h3>
              <p className="text-gray-600 mb-4">Track shipments from pickup to delivery with real-time status updates and proof of delivery.</p>
              <Link to="/features" className="text-blue-600 hover:text-blue-800 font-semibold">Learn more →</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">💰</div>
              <h3 className="text-xl font-semibold mb-3">Trip Costing Software Saudi</h3>
              <p className="text-gray-600 mb-4">Calculate accurate trip costs including fuel, maintenance, and driver expenses per kilometer.</p>
              <Link to="/trip-costing" className="text-blue-600 hover:text-blue-800 font-semibold">Learn more →</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">👥</div>
              <h3 className="text-xl font-semibold mb-3">Driver Management Software Saudi</h3>
              <p className="text-gray-600 mb-4">Manage driver assignments, settlements, and performance tracking.</p>
              <Link to="/features" className="text-blue-600 hover:text-blue-800 font-semibold">Learn more →</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">⛽</div>
              <h3 className="text-xl font-semibold mb-3">Fuel Expense Tracking Software Saudi</h3>
              <p className="text-gray-600 mb-4">Monitor fuel consumption, costs, and efficiency across your entire fleet.</p>
              <Link to="/trip-costing" className="text-blue-600 hover:text-blue-800 font-semibold">Learn more →</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">📊</div>
              <h3 className="text-xl font-semibold mb-3">Reports & Analytics Saudi</h3>
              <p className="text-gray-600 mb-4">Comprehensive reports and analytics for data-driven fleet management decisions.</p>
              <Link to="/features" className="text-blue-600 hover:text-blue-800 font-semibold">Learn more →</Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-4">📱</div>
              <h3 className="text-xl font-semibold mb-3">Arabic & English Support</h3>
              <p className="text-gray-600 mb-4">Full bilingual interface designed for Saudi Arabian fleet operators.</p>
              <Link to="/ar" className="text-blue-600 hover:text-blue-800 font-semibold">View Arabic →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by KSA Fleet Operators
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  ★★★★★
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-lg">"Logistiat helped us reduce our operational costs by 25% through better trip planning and fuel tracking. The Arabic interface makes it easy for our drivers to use."</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-blue-600 font-bold">A</span>
                </div>
                <div>
                  <div className="font-semibold">Ahmed Al-Rashid</div>
                  <div className="text-gray-500">Fleet Manager, Riyadh Transport Co.</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  ★★★★★
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-lg">"The maintenance scheduling feature has eliminated unexpected breakdowns. We now have full visibility into our fleet's health and can plan repairs in advance."</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-blue-600 font-bold">K</span>
                </div>
                <div>
                  <div className="font-semibold">Khalid Al-Mansouri</div>
                  <div className="text-gray-500">Operations Director, Eastern Logistics</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Started with Logistiat
            </h2>
            <p className="text-xl text-gray-600">
              Contact us for a personalized demo of our fleet management system
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-8">
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tell us about your fleet management needs..."
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Logistiat</h3>
              <p className="text-gray-400 mb-4">Fleet management software built for Saudi Arabia</p>
              <p className="text-gray-400">📞 0544288686</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Features</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/features">Shipment Tracking</Link></li>
                <li><Link to="/trip-costing">Trip Costing</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/contact">Contact</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Language</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/en">English</Link></li>
                <li><Link to="/ar">العربية</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Logistiat. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Free Trial Modal */}
      {isTrialModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-4">Start Your Free Trial</h3>
            <p className="text-gray-600 mb-6">
              Get full access to Logistiat for 14 days. No credit card required.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Full Name *"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Company Name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Email Address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="tel"
                placeholder="Phone Number *"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setIsTrialModalOpen(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700"
                >
                  Start Trial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </main>
    </>
  );
};

export default HomePage;